import React from 'react';
import { useSelector } from 'react-redux';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import styles from './asset/accuracyTrend.module.scss';
import { dailyAccuracyTrend$ } from '../../reduxStore/selectors/recordSelectors';

// Loading Skeleton Component
const LoadingSkeleton = () => (
    <div className={styles.loadingSkeleton}>
        <div className={styles.skeletonHeader}>
            <div className={styles.skeletonPulse} style={{ height: '24px', width: '120px' }} />
            <div style={{ display: 'flex', gap: '8px' }}>
                <div className={styles.skeletonPulse} style={{ height: '32px', width: '60px', borderRadius: '8px' }} />
                <div className={styles.skeletonPulse} style={{ height: '32px', width: '150px', borderRadius: '8px' }} />
                <div className={styles.skeletonPulse} style={{ height: '32px', width: '60px', borderRadius: '8px' }} />
            </div>
        </div>
        <div className={styles.skeletonPulse} style={{ height: '250px', width: '100%', marginTop: '20px' }} />
    </div>
);

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const AccuracyTrend = ({ selectedSubject, onSubjectChange, isLoading = false }) => {
    const trendData = useSelector(dailyAccuracyTrend$);

    if (isLoading) {
        return (
            <div className={styles.accuracyTrendDashboard}>
                <LoadingSkeleton />
            </div>
        );
    }

    // Sample data shown when there is no real data yet
    const SAMPLE_LABELS = ['Jan 1', 'Jan 3', 'Jan 5', 'Jan 7', 'Jan 9', 'Jan 11', 'Jan 13'];
    const SAMPLE_DATA = [45, 58, 52, 67, 63, 75, 70];

    const buildSampleDataset = () => ({
        labels: SAMPLE_LABELS,
        datasets: [{
            label: 'Sample',
            data: SAMPLE_DATA,
            borderColor: '#7d7fff',
            backgroundColor: 'rgba(125, 127, 255, 0.08)',
            fill: true,
            tension: 0.4,
            borderDash: [6, 4],
            pointBackgroundColor: '#7d7fff',
            pointBorderColor: '#7d7fff',
            pointRadius: 4,
            pointHoverRadius: 6,
        }],
    });

    // Format data for chart
    const formatChartData = () => {
        // Format dates and filter out null / zero accuracy values
        const labels = [];
        const dataPoints = [];

        if (trendData && trendData.length > 0) {
            trendData.forEach(item => {
                if (item.accuracy !== null && item.accuracy !== 0) {
                    const date = new Date(item.date);
                    const formattedDate = date.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                    });
                    labels.push(formattedDate);
                    dataPoints.push(item.accuracy);
                }
            });
        }

        if (dataPoints.length === 0) {
            return buildSampleDataset();
        }

        return {
            labels,
            datasets: [{
                label: 'Accuracy',
                data: dataPoints,
                borderColor: '#220ec9',
                backgroundColor: 'rgba(34, 14, 201, 0.1)',
                fill: true,
                tension: 0.4,
                pointBackgroundColor: '#220ec9',
                pointBorderColor: '#220ec9',
                pointRadius: 4,
                pointHoverRadius: 6,
            }],
        };
    };

    const isSample = !trendData || trendData.length === 0 ||
        trendData.every(item => item.accuracy === null || item.accuracy === 0);
    const chartData = formatChartData();

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                backgroundColor: '#220ec9',
                titleColor: '#ffffff',
                bodyColor: '#ffffff',
                borderColor: '#220ec9',
                borderWidth: 1,
                cornerRadius: 8,
                displayColors: false,
                callbacks: {
                    label: function (context) {
                        return `${context.parsed.y}%`;
                    }
                }
            },
        },
        scales: {
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    color: '#777e90',
                    font: {
                        family: 'Nourd-SemiBold',
                        size: 12,
                    },
                },
            },
            y: {
                min: 0,
                max: 100,
                grid: {
                    display: false,
                },
                ticks: {
                    color: '#777e90',
                    font: {
                        family: 'Nourd-SemiBold',
                        size: 12,
                    },
                    callback: function (value) {
                        return value + '%';
                    }
                },
            },
        },
    };

    return (
        <div className={styles.accuracyTrendDashboard}>
            <div className={styles.header}>
                <div className={styles.accuracyTrend}>Accuracy Trend</div>
                <div className={styles.subjectToggle}>
                    <button
                        className={`${styles.toggleButton} ${selectedSubject === 'all' ? styles.active : ''}`}
                        onClick={() => onSubjectChange('all')}
                    >
                        All
                    </button>
                    <button
                        className={`${styles.toggleButton} ${selectedSubject === 'Reading & Writing' ? styles.active : ''}`}
                        onClick={() => onSubjectChange('Reading & Writing')}
                    >
                        Reading & Writing
                    </button>
                    <button
                        className={`${styles.toggleButton} ${selectedSubject === 'Math' ? styles.active : ''}`}
                        onClick={() => onSubjectChange('Math')}
                    >
                        Math
                    </button>
                </div>
            </div>
            <div className={styles.chartContainer}>
                <Line data={chartData} options={options} />
                {isSample && (
                    <div className={styles.sampleBadge}>Sample data — start practicing to see your real trend</div>
                )}
            </div>
        </div>
    );
};

export default AccuracyTrend;
